/* ============================================================
   usePublish — zero-knowledge upload and fetch/decrypt boundary

   This is where the client crypto module meets the Worker. Both
   halves of the ZK round-trip live here:

     publishPlan:  plaintext → encrypt (fresh key) → POST /api/new
                   → assemble /plan/<slug>#k=<key>. The key is put in
                   the URL fragment and NEVER sent to the server.
     fetchPlan:    GET /plan/<slug>.json → read #k= from the fragment
                   → decrypt in-browser → plaintext.

   Same-origin fetches (the Worker serves the SPA), so the contract's
   `connect-src 'self'` CSP allows them.
   ============================================================ */

import {
  generateKey,
  encrypt,
  decrypt,
  base64urlDecode,
  buildHash,
  parseHash,
} from '@/crypto'
import type { StoredPlan, ApiResponse, ApiError, Comment } from '@/types'

/** Why a remote plan couldn't be opened — drives the reader's error UI. */
export type PlanLoadError =
  | 'not_found' // 404: no object for this slug
  | 'key_absent' // ciphertext present but no #k= in the URL
  | 'bad_key' // key present but decryption/tag failed (wrong key or tampered)
  | 'fetch_failed' // network / unexpected server error
  | 'malformed' // stored JSON or fragment was structurally invalid

export class PlanLoadFailure extends Error {
  constructor(public readonly code: PlanLoadError) {
    super(code)
    this.name = 'PlanLoadFailure'
  }
}

/** Where a published plan lives on the server, plus the key that opens it. */
export interface PublishResult {
  /** Four BIP39 words, e.g. `quartz-meadow-violin-anchor`. */
  slug: string
  /** The 32-byte AES key. Lives only client-side; never sent to the server. */
  key: Uint8Array
  /** The full shareable URL (`<origin>/plan/<slug>#k=<base64url key>`). */
  url: string
}

/**
 * Encrypt `markdown` under a fresh key and upload it. Returns the slug, the
 * key, and the full shareable URL. The decryption key lives only in the
 * returned fragment — it is never sent to the server.
 */
export async function publishPlan(markdown: string): Promise<PublishResult> {
  const key = generateKey()
  const blob = await encrypt(key, new TextEncoder().encode(markdown))

  const resp = await fetch('/api/new', {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: blob,
  })
  if (!resp.ok) {
    let message = `Upload failed (${resp.status})`
    try {
      const err = (await resp.json()) as ApiError
      if (err?.message) message = err.message
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message)
  }

  const { slug } = (await resp.json()) as ApiResponse
  const fragment = await buildHash(key) // "k=<base64url key>", no leading '#'
  return { slug, key, url: `${location.origin}/plan/${slug}#${fragment}` }
}

/** A decrypted plan plus the key + bundled comments that came in the fragment. */
export interface LoadedPlan {
  /** The decrypted markdown source. */
  markdown: string
  /** The key from the fragment — kept so the reader can re-share this plan. */
  key: Uint8Array
  /** Comments bundled in the fragment by a portable snapshot, or `[]`. */
  comments: Comment[]
  /**
   * Original upload time (ms epoch) from the server record, or `null` when
   * there is no server record (a portable-HTML snapshot). Lets the reader show
   * the plan's real "shared" date instead of the recipient's open time.
   */
  publishedAt: number | null
}

/**
 * Fetch the ciphertext for `slug`, read the key from the URL fragment, and
 * decrypt — entirely in the browser. Returns the markdown plus the key and any
 * portable `&c=` comment bundle. Throws `PlanLoadFailure` with a specific code for each failure
 * mode so the reader can show the right message.
 *
 * `rawHash` is `location.hash` (with or without the leading '#').
 */
export async function fetchPlan(slug: string, rawHash: string): Promise<LoadedPlan> {
  let resp: Response
  try {
    resp = await fetch(`/plan/${encodeURIComponent(slug)}.json`)
  } catch {
    throw new PlanLoadFailure('fetch_failed')
  }
  if (resp.status === 404) throw new PlanLoadFailure('not_found')
  if (!resp.ok) throw new PlanLoadFailure('fetch_failed')

  let ciphertext: string
  let publishedAt: number | null
  try {
    const stored = (await resp.json()) as StoredPlan
    ciphertext = stored.ciphertext
    if (typeof ciphertext !== 'string') throw new Error('no ciphertext')
    // `createdAt` is unix seconds at upload; surface it as ms (or null).
    publishedAt = Number.isFinite(stored.createdAt) ? stored.createdAt * 1000 : null
  } catch {
    throw new PlanLoadFailure('malformed')
  }

  // The key (and any comment bundle) never reached the server; only the
  // fragment carries them.
  const fragment = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash
  let key: Uint8Array | null
  let comments: Comment[] | null
  try {
    ;({ key, comments } = parseHash(fragment))
  } catch {
    // parseHash throws on a malformed key length or a corrupt comment bundle.
    throw new PlanLoadFailure('bad_key')
  }
  if (!key) throw new PlanLoadFailure('key_absent')

  let blob: Uint8Array
  try {
    blob = base64urlDecode(ciphertext)
  } catch {
    throw new PlanLoadFailure('malformed')
  }

  try {
    const plaintext = await decrypt(key, blob)
    return { markdown: new TextDecoder().decode(plaintext), key, comments: comments ?? [], publishedAt }
  } catch {
    // CryptoError: wrong key or tampered ciphertext (GCM tag mismatch).
    throw new PlanLoadFailure('bad_key')
  }
}

/**
 * Decrypt the plan embedded in a downloaded portable-HTML snapshot. The
 * template inlines `base64url(blob)` and the fragment string (`k=…&c=…`) into
 * page globals; there is no server. Mirrors `fetchPlan`'s failure codes.
 */
export async function loadEmbeddedPlan(payloadB64: string, fragment: string): Promise<LoadedPlan> {
  let key: Uint8Array | null
  let comments: Comment[] | null
  try {
    ;({ key, comments } = parseHash(fragment))
  } catch {
    throw new PlanLoadFailure('bad_key')
  }
  if (!key) throw new PlanLoadFailure('key_absent')

  let blob: Uint8Array
  try {
    blob = base64urlDecode(payloadB64)
  } catch {
    throw new PlanLoadFailure('malformed')
  }

  try {
    const plaintext = await decrypt(key, blob)
    // A portable snapshot carries no server record, so there's no upload date.
    return { markdown: new TextDecoder().decode(plaintext), key, comments: comments ?? [], publishedAt: null }
  } catch {
    throw new PlanLoadFailure('bad_key')
  }
}
